import { Brands } from 'src/utils/enums/brands.enum';
import { CreditAppService } from 'src/services/credit-app.service';
import Drawer from 'src/components/ui/drawer/Drawer';
import { financialTypes } from 'src/components/credit/application/FinancialInfo/FinancialInfo';
import { formatDateAndTime } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber';
import { getSubmittedByLabel } from 'src/utils/credit.helpers';
import { Permissions } from 'src/utils/enums/permissions.enum';
import QuickFundDetails from './QuickFundDetails';
import { QuickFundMessageEnum } from 'src/utils/enums/quickFund.enum';
import Spinner from 'src/components/ui/spinner/index';
import Tab from 'src/components/ui/tab/Tab';
import { useDispatch } from 'react-redux';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    CreditApplicationStatus,
    ICreditStatusObject,
} from 'src/services/models/credit.service';
import {
    CreditApplicationType,
    SourceSystem,
    VehicleType,
} from 'src/utils/enums/financialTypes.enum';
import {
    getPermissionsMap,
    isAllow,
    isSiteCanadian,
    maskDigits,
} from 'src/utils/helpers';
import React, { useEffect, useState } from 'react';
import { StatusDot, statusToStatusClass } from 'src/components/ui/statusDot';

import './styles.scss';
interface ICreditDetailProps {
    dealerId: string;
    applicationId: string;
    applicationNumber: number;
    close: VoidFunction;
}

interface ICreditDetail {
    state: ICreditStatusObject;
}

interface ICreditDetailItem {
    title: string;
    rows: Array<{
        rowName: string;
        rowValue: string;
    }> | null;
}
const loader = (
    <div className='loader_container'>
        <Spinner />
    </div>
);

const CreditDetailsItem: React.FC<ICreditDetailItem> = ({ title, rows }) => {
    if (!rows) return <></>;
    return (
        <div className='credit-detail-item u-mt' key={title}>
            <div className='credit-detail-item-title'>
                <h5>{title}</h5>
            </div>
            {rows.map((item, index) => {
                if (item.rowName === '') return;
                return (
                    <React.Fragment key={`${title}-${index}`}>
                        <div className='credit-detail-item-label'>
                            {item.rowName}
                        </div>
                        <div className='credit-detail-item-value'>
                            {item.rowValue}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const CreditDetails: React.FC<ICreditDetail> = ({ state }) => {
    const {
        creditStatusDetails,
        creditStatusFilterResponseToOptionMap,
        vehicleTypeFilterResponseToOptionMap,
        creditSearch,
        financeSection,
    } = useLocalization();
    const status = state?.status;
    const creditApplication = state.creditApplication;
    const isCanadian = creditApplication.ContractCountry == 'Canada';
    const statusObj = (
        <div className='status'>
            <StatusDot color={statusToStatusClass(status)} />
            <span className='dot-label'>
                {creditStatusFilterResponseToOptionMap[status]}
            </span>
        </div>
    );
    function getFinancedAmount() {
        let label = '';
        let amount = '';
        switch (creditApplication.FinanceInformation.FinanceType) {
            case financialTypes.LOAN:
            case financialTypes.BALLOON:
                amount = creditApplication.FinanceInformation.LoanAmount;
                label = creditStatusDetails.COLLATERAL_LABEL_AMOUNT_FINANCED;
                break;
            case financialTypes.LEASE:
                label = isCanadian
                    ? creditStatusDetails.COLLATERAL_LABEL_ADJUSTED_CAP_COST
                    : creditStatusDetails.COLLATERAL_LABEL_AMOUNT_FINANCED;
                amount = creditApplication.FinanceInformation.AdjustedCapCost;
                break;
        }
        return [label, amount];
    }
    function getApplicantRows(applicant: any) {
        if (!applicant) return null;
        const address =
            applicant.BusinessAddress ?? applicant.Residence.Address;
        const homePhoneNumber =
            applicant.HomePhoneNumber?.PhoneNumber ??
            applicant.PhoneNumber?.PhoneNumber;
        return [
            {
                rowName: creditStatusDetails.APPLICANT_LABEL_NAME,
                rowValue:
                    applicant.BusinessName ??
                    `${applicant.ApplicantName.FirstName} ${
                        applicant.ApplicantName.MiddleInitial
                            ? `${applicant.ApplicantName.MiddleInitial} `
                            : ''
                    }${applicant.ApplicantName.LastName}`,
            },
            {
                rowName: creditStatusDetails.APPLICANT_LABEL_ADDRESS,
                rowValue: `${address.AddressLine1}, ${address.City}, ${
                    address.State
                } ${address.ZipCode.Code}${
                    address.ZipCode.CodeExtension ?? ''
                }`,
            },
            {
                rowName: creditStatusDetails.APPLICANT_LABEL_HOME,
                rowValue: formatPhoneNumber(homePhoneNumber),
            },
            {
                rowName: creditStatusDetails.APPLICANT_LABEL_MOBILE,
                rowValue: formatPhoneNumber(
                    applicant.CellPhoneNumber?.PhoneNumber,
                ),
            },
            {
                rowName: creditStatusDetails.APPLICANT_LABEL_SSN,
                rowValue: maskDigits(applicant.IdentificationNumber?.Number),
            },
            {
                rowName: creditStatusDetails.APPLICANT_LABEL_APPLICATION_NUMBER,
                rowValue: state.creditApplication.ApplicationNumber,
            },
        ];
    }
    function getCreditTierValue() {
        return (creditApplication.DecisionInformation.CreditTier ?? '')
            .toString()
            .trim();
    }
    function getDecisionStatus() {
        return creditApplication.DecisionInformation.DecisionStatus;
    }
    function getCreditTierRow() {
        const decisionStatus = getDecisionStatus();
        if (
            isCanadian ||
            decisionStatus == CreditApplicationStatus.DECLINED ||
            decisionStatus == CreditApplicationStatus.WITHDRAWN
        )
            return {
                rowName: '',
                rowValue: '',
            };

        let creditTier: string = getCreditTierValue();
        let creditTierLabel = creditStatusDetails.COLLATERAL_LABEL_TIER;

        if (creditTier === 'F') {
            creditTier = '';
        }

        if (creditTier && decisionStatus === CreditApplicationStatus.PENDING) {
            creditTierLabel = `${creditStatusDetails.COLLATERAL_LABEL_ESTIMATED} ${creditStatusDetails.COLLATERAL_LABEL_TIER}`;
        }

        return {
            rowName: creditTierLabel,
            rowValue: creditTier,
        };
    }
    function getPhoneNumber() {
        let phoneNumber = '';
        const brand = creditApplication.Dealer.Brand;
        if (
            creditApplication.DecisionInformation.ElectronicApprovalMode ==
                'True' ||
            getDecisionStatus() == CreditApplicationStatus.PENDING
        ) {
            if (isCanadian) {
                phoneNumber =
                    brand == Brands.VW ? '800-667-8224' : '877-268-2834';
            } else {
                phoneNumber =
                    brand == Brands.VW ? '800-428-9227' : '888-464-2834';
            }
        } else {
            const { PhoneNumber, Extension } =
                creditApplication.DecisionInformation.CreditAuthorizerPhone;
            phoneNumber = `${PhoneNumber}${
                Extension ? ` ext. ${Extension}` : ''
            }`;
        }
        return phoneNumber;
    }
    function getComments(isStipulation: boolean) {
        const type = isStipulation ? 'Stipulations' : 'DecisionCommentsList';
        const comments = creditApplication.DecisionInformation[type].value;
        if (Array.isArray(comments)) {
            return comments;
        } else if (comments) {
            return [comments];
        }

        return [];
    }
    function hasLTV(str: string) {
        const regex = /(LTV|de l'avance)\s*[:-]?\s*(\d+\.?\d*)/;
        const match = RegExp(regex).exec(str);
        return match;
    }
    function getLtv() {
        const comments = getComments(false);
        for (const comment of comments) {
            const isLtv = hasLTV(comment);
            if (isLtv) {
                return `${isLtv[2]} %`;
            }
        }
        return '-';
    }
    function getCommentList() {
        const decisionComments = getComments(false);
        const stipulationComments = getComments(true);
        const collegeGradComment = state.collegeGradComment;
        const creditTier = getCreditTierValue();
        const desicitionStatus = getDecisionStatus();
        const commentList = [];
        for (const dc of decisionComments) {
            if (!hasLTV(dc)) {
                commentList.push(dc);
            }
        }
        for (const sc of stipulationComments) {
            commentList.push(sc);
        }
        if (collegeGradComment) {
            commentList.push(collegeGradComment);
        }
        if (
            creditTier &&
            (desicitionStatus == CreditApplicationStatus.APPROVED ||
                desicitionStatus == CreditApplicationStatus.CONDITIONED)
        ) {
            commentList.push(creditStatusDetails.DETAIL_LTV_DISCLAIMER);
        }
        return (
            <div className='c-description-list__group'>
                {commentList.map((value, index) => {
                    return (
                        <dt
                            className='c-description-list__detail'
                            key={`${value}-${index}`}
                        >
                            {value}
                        </dt>
                    );
                })}
            </div>
        );
    }
    function getSubmittedByValue() {
        const sourceSystem = creditApplication.SourceSystem;
        let submittedBy = '';
        if (
            sourceSystem == SourceSystem.DEALERTRACK &&
            creditApplication.SalesContact.FirstName == 'DDC'
        ) {
            submittedBy = 'Dealer.com';
        } else {
            submittedBy = creditApplication.SubmittedBy;
        }

        return getSubmittedByLabel(sourceSystem, submittedBy, isCanadian);
    }

    const applicantInformationRows = getApplicantRows(
        creditApplication.PrimaryApplicant ??
            creditApplication.BusinessApplicant,
    );
    const coApplicantInformationRows = getApplicantRows(
        creditApplication.JointOrCoApplicant?.Applicant,
    );
    const coApplicantTitle = getCoApplicantTitle();
    const [financedAmountLabel, amount] = getFinancedAmount();

    function parseFinancyType(financeType: string): string {
        if (
            financeType
                .toLocaleUpperCase()
                .includes(financialTypes.LEASE.toLocaleUpperCase())
        )
            return financeSection.OPTION_LEASE;
        if (
            financeType
                .toLocaleUpperCase()
                .includes(financialTypes.LOAN.toLocaleUpperCase())
        )
            return financeSection.OPTION_LOAN;
        if (
            financeType
                .toLocaleUpperCase()
                .includes(financialTypes.BALLOON.toLocaleUpperCase())
        )
            return creditApplication?.Dealer.Country === 'CA' ||
                creditApplication?.Dealer.Country === 'Canada'
                ? financeSection.OPTION_VWSELECT
                : financeSection.OPTION_DRIVER;
        return financeType;
    }

    const collateralRows = [
        {
            rowName: creditStatusDetails.COLLATERAL_LABEL_TYPE,
            rowValue: parseFinancyType(
                creditApplication.FinanceInformation.FinanceType,
            ),
        },
        {
            rowName: creditStatusDetails.COLLATERAL_LABEL_VEHICLE_TYPE,
            rowValue:
                vehicleTypeFilterResponseToOptionMap[
                    creditApplication.Vehicle.VehicleType as VehicleType
                ],
        },
        {
            rowName: financedAmountLabel,
            rowValue: formatMoney(amount),
        },
        {
            rowName: creditStatusDetails.COLLATERAL_LABEL_TERM,
            rowValue: `${creditApplication.FinanceInformation.Term} ${creditSearch.TABLE_OPTION_MONTHS}`,
        },
        getCreditTierRow(),
        {
            rowName: creditStatusDetails.COLLATERAL_LABEL_LTV,
            rowValue: getLtv(),
        },
        {
            rowName: creditStatusDetails.COLLATERAL_LABEL_VALUE,
            rowValue: formatMoney(creditApplication.FinanceInformation.MSRP),
        },
    ];

    const detailsRows = [
        {
            rowName: creditStatusDetails.DETAIL_LABEL_DEALER,
            rowValue: creditApplication.Dealer.DealerNumber,
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_PHONE,
            rowValue: formatPhoneNumber(getPhoneNumber()),
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_SUBMITTED_BY,
            rowValue: getSubmittedByValue(),
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_SUBMITTED,
            rowValue: formatDateAndTime(creditApplication.SubmittedDate),
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_DECISIONED,
            rowValue: formatDateAndTime(
                creditApplication.DecisionInformation.DecisionDateTime,
            ),
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_CREDIT_AUTHORIZER,
            rowValue: creditApplication.DecisionInformation.CreditAnalyst,
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_CURRENT_STATUS,
            rowValue: statusObj,
        },
        {
            rowName: creditStatusDetails.DETAIL_LABEL_COMMENTS,
            rowValue: getCommentList(),
        },
    ];

    function getCoApplicantTitle() {
        switch (creditApplication.CreditApplicationType) {
            case CreditApplicationType.COAPP:
            case CreditApplicationType.BUSINESS_COAPP:
                return creditStatusDetails.CO_APPLICANT_TITLE;
            case CreditApplicationType.JOINT:
                return creditStatusDetails.JOINT_APPLICANT_TITLE;
            case CreditApplicationType.BUSINESS_PG:
                return creditStatusDetails.GUARANTOR_APPLICANT_TITLE;
            default:
                return creditStatusDetails.CO_APPLICANT_TITLE;
        }
    }

    return (
        <div className='credit-detail-wrapper u-mt u-pl'>
            <div className='credit-detail-header'>
                <h5>{creditStatusDetails.HEADER_LABEL}</h5>
            </div>
            <CreditDetailsItem
                title={creditStatusDetails.APPLICANT_TITLE}
                rows={applicantInformationRows}
            />
            <CreditDetailsItem
                title={coApplicantTitle}
                rows={coApplicantInformationRows}
            />
            <CreditDetailsItem
                title={state.vehicleDescription}
                rows={collateralRows}
            />
            <CreditDetailsItem
                title={creditStatusDetails.DETAIL_TITLE}
                rows={detailsRows}
            />
        </div>
    );
};

const DetailsDrawer: React.FC<ICreditDetailProps> = ({
    dealerId,
    applicationId,
    applicationNumber,
    close,
}) => {
    const DEFAULT_TAB_INDEX = 0;
    const [loading, setLoading] = useState<boolean>(true);
    const [state, setState] = useState<ICreditStatusObject>();
    const [activeTabIndex, setActiveTabIndex] =
        useState<number>(DEFAULT_TAB_INDEX);
    const { creditStatusDetails, creditApp, creditSearch, quickFund } =
        useLocalization();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const permissionsMap = getPermissionsMap();
    const hasCreditAppPermission = isAllow(
        [Permissions.CREDIT_APPLICATION],
        permissionsMap,
        dealerId,
    );
    const showEditButton = hasCreditAppPermission && !!state?.showEditButton;

    useEffect(() => {
        setLoading(true);
        const service = new CreditAppService();
        service
            .creditDetails(dealerId, applicationId.toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setState(response.data);
                    setLoading(false);
                }
            });
    }, []);

    const handleClick = () => {
        close();
        dispatch({
            type: 'CreditDetail',
            payload: {
                creditDetail: state,
            },
        });
        navigate('/dashboard/credit/application');
    };

    async function printApplication() {
        const newTab = window.open('', '_blank');
        if (!newTab) return false;
        const language = i18n.language.includes('fr') ? 'fr-CA' : 'en-CA';
        const applicationData = {
            dealerId: dealerId,
            applicationId: applicationId,
            preferredLanguage: language,
        };

        const service = new CreditAppService();
        let pdfUrl: string;
        try {
            const response = await service.creditApplicationPDF(
                applicationData,
            );
            pdfUrl = URL.createObjectURL(response.data);
            newTab.location.assign(pdfUrl);
            return true;
        } catch (error) {
            newTab.document.body.innerHTML =
                '<h1>Failed to load PDF</h1><p>Please try again later.</p>';
            return false;
        }
    }

    const DrawerTitle = () => {
        return loading ? (
            <></>
        ) : (
            <div className='credit-details-drawer-title'>
                <span>{`${creditSearch.TABLE_HEADER_APPLICATION_NUMBER} `}</span>
                <span className='application-number'>{applicationNumber}</span>
            </div>
        );
    };

    const shouldShowButtons = () => {
        return !loading && activeTabIndex === DEFAULT_TAB_INDEX;
    };

    return (
        <Drawer
            onClose={close}
            actionButton={
                shouldShowButtons()
                    ? {
                          text: creditStatusDetails.DETAILS_BUTTONS_EDIT,
                          showEditButton,
                          onClickHandler: handleClick,
                      }
                    : undefined
            }
            tooltipText={
                shouldShowButtons()
                    ? creditStatusDetails.DETAILS_TOOLTIP
                    : undefined
            }
            alterButton={
                shouldShowButtons()
                    ? {
                          text: creditApp.PRINT_BUTTON,
                          showAlterButton: !loading,
                          onClickHandler: printApplication,
                      }
                    : undefined
            }
            title={<DrawerTitle />}
        >
            {loading || !state ? (
                loader
            ) : (
                <Tab
                    onTabChange={setActiveTabIndex}
                    items={[
                        {
                            label: creditSearch.HEADER_LABEL,
                            content: <CreditDetails state={state} />,
                            selected: true,
                        },
                        ...(!isSiteCanadian() &&
                        state.quickfundData?.reasonMessage !=
                            QuickFundMessageEnum.Other
                            ? [
                                  {
                                      label: quickFund.TITLE,
                                      content: (
                                          <QuickFundDetails
                                              state={state}
                                              onClose={close}
                                          />
                                      ),
                                      selected: false,
                                  },
                              ]
                            : []),
                    ]}
                ></Tab>
            )}
        </Drawer>
    );
};

export default DetailsDrawer;
